import {
  RadioGroup as AriaRadioGroup,
  type RadioGroupProps as AriaRadioGroupProps,
  Button,
  Label,
} from 'react-aria-components';
import { forwardRef, type ReactElement, type ReactNode } from 'react';
import cx from 'clsx';
import type { ForwardRefType } from '#types/forward-ref';
import { InfoIcon } from '#assets/icons/info-icon';
import { Tooltip } from '#components/tooltip/tooltip';
import { Description } from '#components/form-elements/description/description';
import { ErrorMessage } from '#components/form-elements/error-message/error-message';
import styles from './radio-group.module.css';

export interface RadioGroupProps<T extends string> extends Omit<AriaRadioGroupProps, 'onChange'> {
  label?: ReactNode;
  description?: string | ReactNode | null;
  errorMessage?: ReactNode;
  defaultValue?: T;
  value?: T;
  onChange?: (value: T) => void;
  tooltipText?: string;
  labelClassName?: string;
}

export const RadioGroup = (forwardRef as ForwardRefType)(function RadioGroup<T extends string>(
  {
    label,
    description,
    errorMessage,
    children,
    className,
    tooltipText,
    onChange: onChangeProps,
    labelClassName = 'body-standard',
    ...props
  }: RadioGroupProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
): ReactElement {
  const mappedProps = onChangeProps
    ? {
        ...props,
        onChange: (value: string): void => {
          onChangeProps(value as T);
        },
      }
    : props;

  return (
    <AriaRadioGroup
      className={cx(styles.wrapper, className)}
      ref={ref}
      {...mappedProps}
      data-test-radio-group
    >
      {state => (
        <>
          {label ? (
            <Label className={cx(styles['group-label'], labelClassName)}>
              {label}
              {tooltipText ? (
                <Tooltip label={tooltipText} placement="top">
                  <Button aria-label="tooltip" className={cx(styles.tooltip, 'ml-8')}>
                    <InfoIcon />
                  </Button>
                </Tooltip>
              ) : null}
            </Label>
          ) : null}
          {typeof children === 'function' ? children(state) : children}
          {Boolean(description) && <Description>{description}</Description>}
          <ErrorMessage isInvalid={state.isInvalid}>{errorMessage}</ErrorMessage>
        </>
      )}
    </AriaRadioGroup>
  );
});
